import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { Collections, Permissions, Resources, Results, Search } from '@local/client-contracts';
import { ManualPromise } from '@local/common';
import { generateId, isLinkResourceItem, isStaticCollectionItem } from '@local/common-web';
import { KeyName, getExtensionByFileName, getIconByExtension, isHttpOrHttps, isKey, keyCodes } from '@local/ts-infra';
import { UButtonComponent } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventsService, LogService } from '@shared/services';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { isRightClick } from '@shared/utils';
import { Logger } from '@unleash-tech/js-logger';
import { cloneDeep, isEqual } from 'lodash';
import { BehaviorSubject, Subject, Subscription, debounceTime, distinctUntilChanged, take } from 'rxjs';
import { WorkspacesService } from 'src/app/bar/services';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { CollectionsUtilService } from 'src/app/bar/services/collections-util.service';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchOptions, SearchResultContext, SearchService, SearchSession } from 'src/app/bar/services/search';
import { SearchParamsService } from 'src/app/bar/services/search-params.service';
import { CollectionsSourceSettings } from 'src/app/bar/services/search/client';
import { StaticCollectionItemSourceSettings } from 'src/app/bar/services/search/client/static-collection-items';
import { StaticCollectionItemBuilder } from 'src/app/bar/services/search/client/static-collection-items/static-collection-item-builder';
import { ShowToasterService } from 'src/app/bar/services/show-toaster.service';
import { UrlResolverService } from 'src/app/bar/services/url-resolver.service';
import {
  CollectionItem,
  ScrollTrigger,
  SearchResults,
  isCollection,
  isCollectionFileStaticItem,
  isGoLink,
  isPerson,
  isStaticResultItem,
} from '../../../results';
import { ResultSettings } from '../../../results/utils/results-settings';
import { collectionContent } from '../../helpers/collection.content';
import { CreateCollectionType } from '../../models';
import { BlobPreviewService } from '../../services/blobs-preview.service';
import { CollectionPopupService } from '../../services/collection-popup.service';
import { CollectionsComponentFocused } from '../collection-view/collection-view.component';
import { StaticItemsType } from '../new-static-item-popup/new-static-item-popup.component';
import { SearchExpandableButtonComponent } from '../search-expandable-button/search-expandable-button.component';
import { SearchResultsListComponent } from '../search-results-list/search-results-list.component';
import { AddToCollectionPopupService } from 'src/app/bar/services/select-items/add-to-collection-popup.service';
import { DragAndDropData } from '../../../results/components/drag-and-drop/drag-and-drop.component';

export type LoadingData = { position: number; amount: number };
@UntilDestroy()
@Component({
  selector: 'static-collection',
  templateUrl: './static-collection.component.html',
  styleUrls: ['./static-collection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaticCollectionComponent implements OnInit, OnDestroy {
  private readonly TOASTER_ID = 'collection-error';
  private _collection: Collections.StaticCollection;
  private searchSubscription: Subscription;
  private collectionStateOnOpen: Collections.StaticCollection;
  readonly fileMaxSize: number = 5 * 1024 * 1024; //5mb
  private readonly thumbnailExpiredInMs: number = 7 * 24 * 60 * 60 * 1000;
  //keyboard
  private keyHandlerId: string;

  @Input() set collection(val: Collections.StaticCollection) {
    if (!val) return;
    if (!this.collectionStateOnOpen || this.collectionStateOnOpen.id !== val.id) {
      this.collectionStateOnOpen = val;
    }

    const prevCollection = this._collection;
    this._collection = val;
    this.canEdit = this.collectionsHelperService.canEdit(this.collectionsService.currentCollectionView);
    this.disabledDND.set(!this.canEdit);
    this.initComp(false);

    if (!isEqual(prevCollection?.id, this.collection.id)) {
      this.items$.next([]);
      this.searchModel = '';
      if (this.searchButton) {
        this.searchButton.resetData();
      }
      this.clearCheckedItems.emit(true);
      this.collectionChanged.emit(true);
      this.cdr.markForCheck();
      if (this.collection?.items) {
        this.search();
      }
    } else {
      this.collectionChanged.emit(false);
      if (this.collection?.items && !isEqual(prevCollection?.items, this.collection.items)) {
        this.search();
      }
    }

    this.cdr.markForCheck();
  }
  get collection() {
    return this._collection;
  }

  @Input() state: CreateCollectionType;
  @Input() componentFocused: CollectionsComponentFocused;
  @Output() componentFocusedFinish = new EventEmitter();
  @Output() componentFocusedUpdate = new EventEmitter();

  collectionContent = collectionContent;
  createdItemsIds: string[] = [];
  //INIT
  _showAddLinkInput: boolean;
  get showAddLinkInput() {
    return this._showAddLinkInput;
  }
  set showAddLinkInput(val: boolean) {
    if (val) {
      this.componentFocusedUpdate.emit(CollectionsComponentFocused.SEARCH);
    }
    this._showAddLinkInput = val;
    this.cdr.markForCheck();
  }
  showErrorInputAddLink: boolean;
  urlLink: string;
  loadingUrlLink: boolean;
  loadingAmount = 0;
  innerCollections: Collections.Collection[];

  addLoading() {
    this.loadingAmount++;
  }
  removeLoading() {
    this.loadingAmount--;
  }

  CollectionsComponentFocused = CollectionsComponentFocused;

  // Search
  private searchSession: SearchSession;
  private searchInnerCollectionSession: SearchSession;
  displayedContext$ = new BehaviorSubject<SearchResultContext>(null);
  _items: (Search.CollectionResultItem | Search.ResultItem)[];
  items$ = new Subject<(Search.CollectionResultItem | Search.ResultItem)[]>();
  itemsAmount = 0;
  searchModel: string;
  formInputChanged = new Subject<string>();
  loadingItem: LoadingData = { position: 0, amount: this.loadingAmount };
  emptyResults$ = new Subject<boolean>();
  copiedItemsIds: string[] = [];
  @ViewChild(SearchExpandableButtonComponent) searchButton: SearchExpandableButtonComponent;

  //checkbox
  clearCheckedItems: EventEmitter<any> = new EventEmitter();
  checkedItems: Set<string>;

  private staticCollectionItemBuildResultView: StaticCollectionItemBuilder;
  protected logger: Logger;
  private selectedIndex: number;

  //drag and drop
  disabledDND = signal<boolean>(false);
  disabledPaste = signal<boolean>(false);
  dndItemsLoadingAmount: number;

  @ViewChild('plusButton') plusButton: UButtonComponent;
  @ViewChild('emptyStateButton') emptyStateButton: UButtonComponent;
  collectionChanged = new EventEmitter();

  //preview
  previewMode: boolean;
  showPreviewButton: boolean;
  @ViewChild(SearchResultsListComponent) searchResultsComp: SearchResultsListComponent;

  @HostBinding('class.can-edit')
  get isCanEdit() {
    return this.canEdit;
  }

  //layout
  @HostBinding('attr.layoutMode')
  @Input()
  layoutMode: Results.LayoutType;
  @Input() updateHeight: EventEmitter<void>;

  @Output() viewModeChanged = new EventEmitter();
  isPreviewPressed: boolean;

  @HostListener('auxclick', ['$event']) onAuxClick(event) {
    const parentContainer = ['ng-scroll-layer', 'static-collections-container'];
    if (isRightClick(event) && parentContainer.includes(event.target.parentElement.className)) {
      this.addNewItem(event, 'right-click');
    }
  }

  get previewHandlerService() {
    return this.searchResultsComp?.previewHandlerService;
  }

  constructor(
    private hubService: HubService,
    private cdr: ChangeDetectorRef,
    public collectionPopupService: CollectionPopupService,
    public collectionsService: CollectionsService,
    private eventsService: EventsService,
    private searchService: SearchService,
    private urlResolverService: UrlResolverService,
    private blobsService: BlobsService,
    logService: LogService,
    private blobPreviewService: BlobPreviewService,
    private showToasterService: ShowToasterService,
    private searchParamsService: SearchParamsService,
    private keyboardService: KeyboardService,
    private collectionsHelperService: CollectionsUtilService,
    private workspaceService: WorkspacesService,
    private filtersService: FiltersService,
    private avatarListService: AvatarListService,
    private AddToCollectionPopupService: AddToCollectionPopupService
  ) {
    this.searchSession = this.searchService.getOrCreateSearchSession('static-collections');
    this.logger = logService.scope('StaticCollectionComponent');
    this.staticCollectionItemBuildResultView = new StaticCollectionItemBuilder(
      this.collectionsService,
      this.collectionsHelperService,
      this.blobsService,
      this.workspaceService,
      this.avatarListService,
      this.urlResolverService
    );
  }

  ngOnInit(): void {
    this.initComp();
    this.searchModel = '';
    this.formInputChanged.pipe(debounceTime(300), untilDestroyed(this)).subscribe((search) => {
      this.onSearchItems(search);
    });
    this.items$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr))
      )
      .subscribe((items) => {
        this._items = items;
        if (!this.searchInnerCollectionSession) {
          this.initResultCountCollections();
        }
        this.cdr.markForCheck();
      });
    this.registerKeyboardHandler();
  }

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
    this.searchSession.destroy();
    this.copiedItemsIds = [];
  }

  canEdit: boolean;

  initComp(resetAddLinkInput = true) {
    if (resetAddLinkInput) {
      this.showAddLinkInput = false;
    }
    this.showErrorInputAddLink = false;
    this.urlLink = null;
    this.loadingUrlLink = false;
  }

  private initResultCountCollections() {
    this.searchInnerCollectionSession = this.searchService.getOrCreateSearchSession('static-collection-collections');
    const source = ResultSettings.defaultCollections;
    const searchTrigger = this.hubService.getState('search-trigger')?.[0];
    const collectionIds = this._items?.filter((i) => isCollection(i))?.map((c: CollectionItem) => c.id) || [];
    const options: SearchOptions = {
      resetSession: true,
      query: this.hubService.query?.toLowerCase(),
      sources: [
        {
          ...source,
          collectionIds,
          sorting: this.searchParamsService.getSort(),
          filters: { preFilters: this.filtersService.getPreFilters(true), postFilters: this.filtersService.postFilters },
          tag: 'get-collections-container',
        } as CollectionsSourceSettings,
      ],
      trigger: searchTrigger ? searchTrigger : 'collections/user_query',
    };
    this.searchInnerCollectionSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext) => {
        if (ctx.searchCompleted) {
          this.innerCollections = ctx?.items?.filter((i) => i.type === 'collection') as Collections.Collection[];
          if (this.innerCollections?.length) {
            this.initResultCountItems();
          }
        }
      });
  }

  onChangeAddLink($event) {
    this.showErrorInputAddLink = false;
    this.urlLink = $event;
    this.cdr.markForCheck();
  }

  onLinkTelemetry(label: string) {
    this.eventsService.event('collections.collection_link', {
      label,
      location: { title: this.hubService.currentLocation },
    });
  }

  private resetAllEditModeItems() {
    if (!this._items?.length) return;
    const resetItems = this._items.map((item) => {
      const newItem = item;
      if (item.showResultSections) {
        newItem.showResultSections = { ...item.showResultSections, showInputTitle: false };
      }
      return newItem;
    });
    this.items$.next(resetItems);
  }

  addNewItem(event, trigger: 'button' | 'empty-state' | 'right-click') {
    const position = this.getPopupPositionByTrigger(event, trigger);
    const ref = this.collectionsService.openNewStaticItemPopup(position, trigger);
    ref.compInstance.onClickEvent.pipe(take(1)).subscribe((type: StaticItemsType) => {
      if (type === 'link') {
        this.showAddLinkInput = true;
      } else if (type === 'header') {
        this.addNewCollectionHeaderItem('Header');
      }
    });
    ref.compInstance.onUploadEvent.pipe(take(1)).subscribe((event: File) => {
      this.addNewFileItem(event);
    });
    ref.compInstance.onUploadErrorEvent.pipe(take(1)).subscribe((event: string) => {
      this.showErrorUploadFile(event);
    });
  }

  private getPopupPositionByTrigger(event, trigger: 'button' | 'empty-state' | 'right-click') {
    let position = { x: 0, y: 0 };
    switch (trigger) {
      case 'button':
        position = this.plusButton.button.nativeElement.getBoundingClientRect();
        break;
      case 'empty-state':
        position = this.emptyStateButton.button.nativeElement.getBoundingClientRect();
        break;
      default:
        position = { x: event.x, y: event.y };
        break;
    }
    return position;
  }

  onAddLink(showErrorToaster = false, position = 0) {
    if (this.loadingUrlLink) {
      return;
    }
    let urlLink = this.urlLink;
    if (this.urlLink && this.urlLink.length > 0) {
      if (!isHttpOrHttps(urlLink)) {
        urlLink = `https://${urlLink}`;
      }
      this.onLinkTelemetry('add');
      this.setLoadingState(position, 'add');
      this.urlResolverService
        .resolve(urlLink)
        .then(async (res: Search.ResultResourceItem | Resources.WebsiteMetadata) => {
          if (!res) {
            this.showErrorAddLink(showErrorToaster);
            this.setLoadingState(position, 'remove');
            return;
          }
          await this.addNewCollectionUrlItem(res, urlLink, position);
          this.setLoadingState(position, 'remove');
          this.initComp();
          this.cdr.markForCheck();
        })
        .catch((err) => {
          this.showErrorAddLink(showErrorToaster);
          this.logger.error('onAddLink error', err);
          this.setLoadingState(position, 'remove');
        });
    } else {
      this.initComp();
      this.cdr.markForCheck();
    }
  }

  showErrorAddLink(showErrorToaster: boolean) {
    if (showErrorToaster) {
      this.showToasterService.showErrorToaster(this.TOASTER_ID, collectionContent.addLinkInputError);
    } else {
      this.showErrorInputAddLink = true;
    }
    this.cdr.markForCheck();
  }

  showErrorUploadFile(errorMessage: string) {
    this.showToasterService.showErrorToaster(this.TOASTER_ID, errorMessage);
    this.cdr.markForCheck();
  }

  setLoadingState(position: number, state: 'add' | 'remove' | 'none') {
    if (state === 'add') {
      this.addLoading();
    } else if (state === 'remove') {
      this.removeLoading();
    }
    const amount = this.loadingAmount;
    const data: LoadingData = { position, amount };
    this.loadingUrlLink = amount !== 0;
    this.loadingItem = data;
    this.cdr.markForCheck();
  }

  private async addNewCollectionUrlItem(res: Search.ResultResourceItem | Resources.WebsiteMetadata, urlLink: string, position = 0) {
    this.resetAllEditModeItems();
    let item: Collections.UrlItem | Collections.LinkResourceItem;
    const addedTime = Date.now();
    const showAddedTime = this.state !== 'new';
    if (res.type === 'WebsiteMetadata') {
      const now = Date.now();
      const currentUser = this.collectionsService.sessionInfo?.user;
      const urlItem: Collections.UrlItem = {
        kind: 'url',
        url: urlLink,
        id: generateId(),
        modifiedTime: now,
        modifiedBy: currentUser?.id,
        title: res?.title === '' || !res?.title ? 'Untitled' : res.title,
        collectionId: this.collection.id,
        createdTime: now,
        thumbnail: res?.banner,
        thumbnailExpired: res?.banner ? Date.now() + this.thumbnailExpiredInMs : undefined,
        addedTime,
        showAddedTime,
      };
      item = urlItem;
    } else {
      item = {
        ...((await this.AddToCollectionPopupService.getLinkResourceStaticItem(this.collection.id, res)) as Collections.LinkResourceItem),
        showAddedTime,
      };
    }
    const items = this.collection.items ? cloneDeep(this.collection.items) : [];
    items.splice(position, 0, item);
    this.updateNewItem(items, items, 'Update');
    this.createdItemsIds.push(item.id);
  }

  private async addNewCollectionHeaderItem(text: string, isCopy = false) {
    this.resetAllEditModeItems();
    if (!isCopy) {
      this.eventsService.event('collections.collection_actions', {
        target: 'add',
        label: 'header',
        location: { title: this.hubService.currentLocation },
      });
    }

    const id = generateId();
    const item: Collections.HeaderItem = {
      kind: 'header',
      text,
      id,
      addedTime: Date.now(),
      showAddedTime: this.state !== 'new',
    };
    const items = this.collection.items ? cloneDeep(this.collection.items) : [];
    items.unshift(item);
    this.updateNewItem(items, items, 'Update');
    this.createdItemsIds.push(id);
    if (isCopy) {
      this.copiedItemsIds.push(id);
    }
  }

  private async addNewFileItem(file: File, position = 0) {
    this.resetAllEditModeItems();
    const { name, size, type } = file;
    this.eventsService.event('collections.upload', {
      name: 'edit_collection',
      target: 'resource',
      label: 'add',
      location: {
        title: this.hubService.currentLocation,
      },
      jsonData: JSON.stringify({ size, content_type: type }),
    });
    try {
      this.setLoadingState(position, 'add');
      const shareOptions: Permissions.ShareOptions = {
        level: 'Following',
        followingIds: [this.collection.id],
      };
      const blobId = (await this.blobsService.create(name, type, shareOptions))?.id;
      const uploadResponse = await this.blobsService.upload(file, blobId);
      if (uploadResponse.status >= 400) {
        this.onFileUploadError(name);
        return;
      }
      this.addFileToCollectionItems(file, blobId, position);
      this.setLoadingState(position, 'remove');
    } catch {
      this.setLoadingState(position, 'remove');
      this.onFileUploadError(name);
    }
  }

  private onFileUploadError(name: string, errorMessage?: string) {
    const message = errorMessage ? errorMessage : `Upload of file "${name}" failed`;
    this.showErrorUploadFile(message);
    this.eventsService.event('hub_error_modal', {
      name: 'show',
      label: 'upload_failed',
    });
  }

  addFileToCollectionItems(file: File, fileId: string, position = 0) {
    const { name, size, type } = file;
    const extension = getExtensionByFileName(name);
    const icon = { lightUrl: getIconByExtension(extension) };
    const id = generateId();
    const currentUser = this.collectionsService.sessionInfo?.user;
    const item: Collections.FileItem = {
      kind: 'file',
      fileId,
      name,
      size,
      extension: {
        type,
        ending: extension,
      },
      id,
      uploadTime: Date.now(),
      uploadBy: currentUser.id,
      icon,
      collectionId: this.collection.id,
      addedTime: Date.now(),
      showAddedTime: this.state !== 'new',
    };
    const items = this.collection.items ? cloneDeep(this.collection.items) : [];
    items.splice(position, 0, item);
    this.updateNewItem(items, items, 'Update');
    this.createdItemsIds.push(id);
  }

  checkedItemsUpdate($event) {
    this.eventsService.event('collections.select_remove_item_collection', {
      location: { title: this.hubService.currentLocation },
    });
    this.checkedItems = $event;
    this.cdr.markForCheck();
  }

  onSearchItems($event) {
    $event = !$event || $event.length === 0 ? null : $event;
    this.searchModel = $event;
    this.hubService.query = $event;
    this.search();
  }

  titleChanged($event: { value: string; item: Search.ResultItem }) {
    this.disabledPaste.set(false);
    const itemId = $event.item.id;
    this.createdItemsIds = this.createdItemsIds.filter((id) => id !== itemId);
    const items = cloneDeep(this.collection.items);
    const itemIndex = items.findIndex((c) => c.id === itemId || (isLinkResourceItem(c) && c.searchResource?.id === itemId));
    if (itemIndex === -1) {
      return;
    }
    const item = items[itemIndex];
    if ($event.item.type === 'collection-header') {
      if ((<Collections.HeaderItem>items[itemIndex]).text === $event.value) {
        return;
      }
      this.eventsService.event('collections.action', {
        target: 'edit',
        label: 'header',
        location: { title: this.hubService.currentLocation },
      });

      items[itemIndex] = this.staticCollectionItemBuildResultView.updateCollectionHeaderItemTitle(
        <Collections.HeaderItem>items[itemIndex],
        $event.value
      );
    } else if (isCollectionFileStaticItem($event.item)) {
      const item = <Collections.FileItem>items[itemIndex];
      if (item.name === $event.value) {
        return;
      }
      const newItem = this.staticCollectionItemBuildResultView.updateCollectionFileItemTitle(item, $event.value);
      items[itemIndex] = newItem;
      const fileEnding = `.${item.extension?.ending}`;
      const metaDataName = newItem.name.endsWith(fileEnding) || !item.extension?.ending ? newItem.name : `${newItem.name}${fileEnding}`;
      this.blobsService.update(item.fileId, metaDataName, item.extension?.type);
    } else if (isLinkResourceItem(item)) {
      if (item.searchResource.view.title.text === $event.value) {
        return;
      }
      item.searchResource.view.title.text = $event.value;
    } else {
      if ((<Collections.UrlItem>items[itemIndex]).title === $event.value) {
        return;
      }
      items[itemIndex] = this.staticCollectionItemBuildResultView.updateCollectionUrlItemTitle(
        <Collections.UrlItem>items[itemIndex],
        $event.value
      );
    }
    this.updateNewItem(items[itemIndex], items, 'SetUpdate');
  }

  onRenameFlow($event) {
    if ($event) {
      this.disabledPaste.set(true);
    }
  }

  onDynamicCommand($event: { value: string; item: Search.ResultItem }) {
    switch ($event.value) {
      case 'remove':
        this.openDeletePopup(() => this.removeFromCommand($event.item), [$event.item]);
        break;
      case 'rename':
        this.renameFromCommand($event.item);
        break;
      case 'copy':
        this.makeCopyFromCommand($event.item);
        break;
      default:
        break;
    }
  }

  makeCopyFromCommand(item) {
    this.addNewCollectionHeaderItem(item.view.title.text, true);
  }

  removeFromCommand(item: Search.ResultItem) {
    const items = cloneDeep(this.collection.items);
    const itemIndex = items.findIndex((c) => {
      if (isStaticCollectionItem(c)) {
        return c.id === item.id;
      } else if (isLinkResourceItem(c)) {
        return c.searchResource.id === item.id;
      }
    });
    if (itemIndex === -1) {
      return;
    }
    const itemToDelete = items[itemIndex];
    items.splice(itemIndex, 1);
    this.updateNewItem(itemToDelete, items, 'SetRemove');
    this.clearCheckedItems.emit(item.id);
  }

  renameFromCommand(item: Search.ResultItem) {
    const items = this._items;
    const itemIndex = items.findIndex((c) => {
      return c.id === item.id;
    });
    if (itemIndex === -1) {
      return;
    }
    this.disabledPaste.set(true);

    items[itemIndex] = <Search.ResultItem>{
      ...items[itemIndex],
      showResultSections: {
        ...items[itemIndex].showResultSections,
        showInputTitle: true,
      },
    };
    this.items$.next([...items]);
  }

  onDragChange($event: { currentIndex: number; previousIndex: number }) {
    this.eventsService.event('collections.drag_collection', {
      location: { title: this.hubService.currentLocation },
    });
    const items = [...this.collection.items];
    const item = items[$event.previousIndex];
    items.splice($event.previousIndex, 1);
    items.splice($event.currentIndex, 0, item);
    this.updateNewItem(items, items, 'Update');
  }

  //CRUD ACTIONS
  updateNewItem(
    item: Collections.StaticItem | Collections.StaticItem[],
    items: Collections.StaticItem[],
    type: Collections.CollectionItemAction = 'SetAdd'
  ) {
    const updateAction: Collections.UpdateAction = { field: 'items', type, value: item, idField: 'id' };
    this.collectionsService.updateCollection(this.collection.id, [updateAction], false);
    this.collectionsService.currentCollectionView = <Collections.StaticCollection>{ ...this.collection, items };
    this.cdr.markForCheck();
  }

  onDeleteItems() {
    this.eventsService.event('collections.remove_item_collection', {
      location: { title: this.hubService.currentLocation },
      label: 'remove',
    });
    const deleteActions: Collections.UpdateAction[] = [];
    this.checkedItems.forEach((itemId) => {
      const item = this.collection.items.find((i) => i.id === itemId);
      if (item) {
        deleteActions.push({ field: 'items', type: 'SetRemove', value: item, idField: 'id' });
      }
    });
    let items = cloneDeep(this.collection.items);
    items = items.filter((val) => !this.checkedItems.has(val.id));
    this.collectionsService.updateCollection(this.collection.id, deleteActions, false);
    this.checkedItems.clear();
    this.collectionsService.currentCollectionView = <Collections.StaticCollection>{ ...this.collection, items };
    this.clearCheckedItems.emit(true);
    this.cdr.markForCheck();
  }

  onSelectedIndexChanged(newIndex) {
    this.selectedIndex = newIndex;
  }

  onDelete() {
    const constItemsToDelete = [];
    this.checkedItems.forEach((itemId) => {
      const item = this.collection.items.find((i) => i.id === itemId);
      constItemsToDelete.push(item);
    });
    this.openDeletePopup(() => this.onDeleteItems(), constItemsToDelete, this.checkedItems?.size);
  }

  async onDropItems(data: DragAndDropData) {
    if (data.pasteItem) {
      return this.onPasteItem(data.pasteItem);
    }

    const largeFilesNames = [];

    const validFiles = data.files?.filter((file) => {
      if (file.size > this.fileMaxSize) {
        largeFilesNames.push(file.name);
        return false;
      }
      return true;
    });

    largeFilesNames.forEach((name) => {
      this.onFileUploadError(name, 'Upload failed. Max file size of 5MB.');
    });

    if (!validFiles.length) {
      return;
    }

    this.dndItemsLoadingAmount = validFiles?.length;

    this.sendDropEvent(data.files);

    this.sortDndItems(validFiles).map((file) => {
      this.addNewFileItem(file, 0);
    });
  }

  async onPasteItem(item: DataTransferItem) {
    const fileType = item.type;
    this.dndItemsLoadingAmount = 1;
    const manualPromise = new ManualPromise<string>();
    item.getAsString((s) => manualPromise.resolve(s));
    const plainContent = await manualPromise;
    const plainContentLines = plainContent
      .replaceAll('\r', '')
      .split('\n')
      .filter((x) => x)
      .map((x) => x.trim());
    if (plainContentLines.length && plainContentLines.every((l) => l.startsWith('www.') || isHttpOrHttps(l))) {
      for (const l of plainContentLines) {
        this.urlLink = l;
        this.onAddLink(false, this.selectedIndex);
      }
      return;
    }

    let title = (plainContentLines?.[0] || 'My File').substring(0, 150);
    if (fileType == 'text/html') {
      title += '.html';
    } else if (fileType?.startsWith('text/')) {
      title += '.txt';
    }

    const newFile = new File([plainContent], title, { type: fileType });
    return this.addNewFileItem(newFile, this.selectedIndex);
  }

  private sendDropEvent(files: File[]) {
    files?.forEach((file) => {
      const jsonData = JSON.stringify({ size: file?.size, content_type: file.type });
      this.eventsService.event('collections.drag_drop', {
        location: { title: this.hubService.currentLocation },
        jsonData,
      });
    });
  }

  sortDndItems(files: File[]): File[] {
    return files.sort((a, b) => {
      if (a.type == 'text/html') return -1;
      else if (b.type == 'text/html') return 1;
      if (a.type == 'text/rtf') return -1;
      else if (b.type == 'text/rtf') return 1;
      if (a.type == 'text/plain') return -1;
      else if (b.type == 'text/plain') return 1;
      if (a.type.startsWith('image/')) return -1;
      else if (b.type.startsWith('image/')) return 1;
      return -1;
    });
  }

  openDeletePopup(actionFunc: Function, items, itemsCount = 1) {
    const deletePopupRef = this.collectionPopupService.openAreYouSureToRemoveItemPopup(items, itemsCount);
    deletePopupRef.compInstance.primaryButton.pipe(take(1)).subscribe(() => {
      actionFunc();
    });
  }

  //@@@@ SEARCH PART

  private search() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
      this.searchSubscription = null;
    }
    const options: SearchOptions = {
      resetSession: false,
      query: this.searchModel,
      sources: [
        {
          id: 'static-collection-items',
          collection: this.collection,
          collectionId: this.collection.id,
          type: 'static-collection-items',
          noHeader: true,
        } as StaticCollectionItemSourceSettings,
      ],
      trigger: 'navigation_tree',
      telemetrySearchMethod: 'Quick-Search',
    };
    this.searchSubscription = this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext) => {
        if (ctx?.searchCompleted) {
          this.onSearchCompleted(ctx);
        }
      });
  }

  async onSearchCompleted(ctx: SearchResultContext) {
    let items = ctx.items;
    if (!this.loadingAmount) {
      items = ctx.items.map((item) => {
        return this.buildViewItem(item);
      });
      items = this.removeMultipleEditModesIfNeeded(items);
    }
    this.displayedContext$.next({ ...ctx, items });
    this.items$.next(items as any);
    this.itemsAmount = items.filter((item) => item.type !== 'collection-header')?.length;
    this.collectionsService.collectionResultData$.next({ count: this.itemsAmount });
    this.emptyResults$.next(ctx && ctx.searchCompleted && ctx.items?.length == 0 && this.searchModel?.length > 0);
    this.cdr.markForCheck();
  }

  private initResultCountItems(items?: SearchResults[]) {
    if (!this.innerCollections?.length || (!this._items && !items)) {
      return;
    }
    if (!items) {
      items = cloneDeep(this._items);
    }
    for (const item of items) {
      if (isCollection(item)) {
        item.resultsCount = this.innerCollections.find((c) => c.id === item.id)?.resultsCount;
      }
    }
    const itemsChanged = !isEqual(items, this._items);
    if (itemsChanged) {
      this.items$.next(items as any);
    }
  }

  private removeMultipleEditModesIfNeeded(items) {
    const editModeItems = items.filter((item) => item.showResultSections?.showInputTitle);
    this.disabledPaste.set(editModeItems?.length > 0);
    if (editModeItems?.length <= 1 && this.dndItemsLoadingAmount > 1) {
      this.dndItemsLoadingAmount = 0;
    } else if (this.dndItemsLoadingAmount <= 1) {
      this.dndItemsLoadingAmount = 0;
      if (editModeItems?.length <= 1) {
        return items;
      }
    } else if (editModeItems?.length <= 1) {
      return items;
    }
    return items.map((item) => {
      const newItem = { ...item, showResultSections: { ...item.showResultSections, showInputTitle: false } };
      return newItem;
    });
  }

  buildViewItem(item) {
    if (!this.canEdit || item.type === 'collection') {
      return item;
    }
    if ((item as any).type === 'collection-url') {
      return this.buildUrlItemView(item);
    }
    if ((item as any).type === 'collection-header') {
      return this.buildHeaderView(item);
    }
    if (isCollectionFileStaticItem(item)) {
      return this.buildFileView(item);
    }
    if (isStaticResultItem(item) && !isPerson(item) && !isGoLink(item)) {
      return this.buildStaticItemView(item);
    }
    return { ...item, dynamicCommands: ['remove'] };
  }

  buildStaticItemView(item: Search.StaticCollectionResultItem) {
    const showInputTitle = !!this.createdItemsIds.find((id) => id === item.id);
    return { ...item, showResultSections: { ...item.showResultSections, showInputTitle }, dynamicCommands: ['rename', 'remove'] };
  }

  private buildFileView(item: Search.CollectionFileResultItem): Search.CollectionFileResultItem {
    const showInputTitle = !!this.createdItemsIds.find((id) => id === item.id);
    return { ...item, showResultSections: { ...item.showResultSections, showInputTitle } };
  }

  private buildHeaderView(item: Search.CollectionResultItem): Search.CollectionResultItem {
    const isCopiedItem = !!this.copiedItemsIds?.find((id) => item.id === id);
    let showInputTitle = !!this.createdItemsIds.find((id) => id === item.id);
    if (isCopiedItem) {
      showInputTitle = false;
    }
    return { ...item, showResultSections: { ...item.showResultSections, showInputTitle } };
  }

  private buildUrlItemView(item: Search.CollectionResultItem): Search.CollectionResultItem {
    const showInputTitle = !!this.createdItemsIds.find((id) => id === item.id);
    return { ...item, showResultSections: { ...item.showResultSections, showInputTitle } };
  }

  nextPage(trigger: ScrollTrigger) {
    this.searchSession
      .nextPage$(trigger, [{ id: 'static-collection-items', type: 'static-collection-items' }])
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext[]) => {
        if (ctx[0]?.searchCompleted) {
          this.onSearchCompleted(ctx[0]);
        }
      });
  }

  changeLayout() {
    this.searchParamsService.layoutMode = this.layoutMode === 'list' ? 'gallery' : 'list';
    this.viewModeChanged.emit();
  }

  //@@@ KEY HANDLING

  registerKeyboardHandler() {
    if (this.keyHandlerId) return;
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      this.handleKeys(keys, event);
    }, 9);
    this.cdr.markForCheck();
  }

  private unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.keyHandlerId = null;
  }

  async handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent) {
    if (isKey(event, keyCodes.ArrowDown)) {
      this.componentFocusedUpdate.emit(CollectionsComponentFocused.RESULTS);
    }
  }
}
